import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Announcement {
  id: string
  title: string
  announcement_type: string
  priority: string
  author: string
  publish_date: string
  status: string
  views_count: number
  acknowledgements: number
  is_pinned: boolean
}

interface Channel {
  id: string
  name: string
  type: string
  members_count: number
  description: string
}

export default function CommunicationsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [channels, setChannels] = useState<Channel[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [annRes, chanRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/communications/announcements'),
      fetch(API_BASE + '/communications/channels'),
      fetch(API_BASE + '/communications/statistics')
    ])
    if (annRes.ok) setAnnouncements((await annRes.json()).announcements || [])
    if (chanRes.ok) setChannels((await chanRes.json()).channels || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'expired': return 'bg-gray-100 text-gray-600'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600'
      case 'normal': return 'text-blue-600'
      case 'low': return 'text-gray-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Communications Hub</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Announcement</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Channels</div>
              <div className="text-2xl font-bold">{statistics.total_channels}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Announcements</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_announcements}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">High Priority</div>
              <div className="text-2xl font-bold text-red-600">{statistics.high_priority_announcements}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Messages Today</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_messages_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unread</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.unread_messages}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Announcements</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {announcements.slice(0, 10).map(ann => (
                  <tr key={ann.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {ann.is_pinned && <span className="text-yellow-500">📌</span>}
                        <div>
                          <div className="font-medium">{ann.title}</div>
                          <div className="text-xs text-gray-500">{ann.author} • {ann.publish_date}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm capitalize">{ann.announcement_type}</div>
                      <span className={`text-xs ${getPriorityColor(ann.priority)}`}>{ann.priority} priority</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{ann.views_count} views</div>
                      <div className="text-xs text-green-600">{ann.acknowledgements} acks</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ann.status)}`}>{ann.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Channels</h3>
            <div className="space-y-3">
              {channels.map(ch => (
                <div key={ch.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{ch.name}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{ch.type}</span>
                  </div>
                  <div className="text-xs text-gray-500">{ch.description}</div>
                  <div className="text-xs text-gray-600 mt-2">{ch.members_count} members</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
